import { type Editor } from "@tiptap/core";

// Collapse blank lines and empty <p></p> blocks that sit between two consecutive
// list items, which otherwise split the list and render as vertical gaps. Only the
// space between list items is touched — a blank line before a list (after a heading
// or paragraph) and intentional empty paragraphs in prose are left intact.
export function tightenListSpacing(markdown: string): string {
  const betweenItems = /^([ \t]*(?:[-*+]|\d+\.)[ \t][^\n]*)\n((?:[ \t]*(?:<p><\/p>)?[ \t]*\n)+)(?=[ \t]*(?:[-*+]|\d+\.)[ \t])/gm;
  let previous: string;
  do {
    previous = markdown;
    markdown = markdown.replace(betweenItems, "$1\n");
  } while (markdown !== previous);
  return markdown;
}

export function preserveEmptyParagraphs(markdown: string): string {
  const withParagraphs = markdown.replace(/\n{3,}/g, (match) => {
    const emptyParagraphCount = Math.max(1, Math.floor((match.length - 2) / 2));
    return "\n\n" + "<p></p>\n\n".repeat(emptyParagraphCount);
  });
  return tightenListSpacing(withParagraphs);
}

// Two markdown strings are equivalent when they produce the same editor document.
// The stored/persisted memo is normalized (list spacing tightened, empty paragraphs
// expanded) while `editor.storage.markdown.getMarkdown()` returns the raw form, so a
// direct string comparison reports a difference for content that is actually identical.
// Comparing both sides after normalization avoids redundant `setContent` calls, which
// would otherwise re-render the whole document and visibly remount node views such as
// generated chart images (causing them to flicker/jump).
export function memoContentEquivalent(a: string, b: string): boolean {
  return preserveEmptyParagraphs(a) === preserveEmptyParagraphs(b);
}

export function patchParagraphSerializer(editor: Editor): void {
  const serializer = editor.storage.markdown?.serializer;
  if (!serializer) return;

  const proto = Object.getPrototypeOf(serializer);
  const protoDescriptor = Object.getOwnPropertyDescriptor(proto, "nodes");
  if (!protoDescriptor?.get) return;

  const ownDescriptor = Object.getOwnPropertyDescriptor(serializer, "nodes");
  const baseGet = ownDescriptor?.get ?? protoDescriptor.get;

  Object.defineProperty(serializer, "nodes", {
    configurable: true,
    get() {
      const nodes = baseGet.call(this);
      const origParagraph = nodes.paragraph;
      if (origParagraph) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        nodes.paragraph = (state: any, node: any, parent: any, index: number) => {
          if (node.childCount === 0) {
            state.write("<p></p>");
            state.closeBlock(node);
          } else {
            origParagraph(state, node, parent, index);
          }
        };
      }
      return nodes;
    }
  });
}
